/**
 * Theme adapter for NPR US site — injects real next/font families into the foundation.
 */
import type { Theme } from '@emotion/react';
import { buildTheme } from 'common-ui';
import type { CommonThemeVars, ThemeCreateFunction } from 'tsconfig/types.d';
import { gothamFont, robotoFont } from '../common/nextFont';
import {
  buildThemeVarsUsNPR as buildThemeVarsUsNPRFoundation,
  buildThemeMixinsUsNPR as buildThemeMixinsUsNPRFoundation,
  buildThemeWidgetStylesUsNPR as buildThemeWidgetStylesUsNPRFoundation,
  buildThemeWidgetStylesUsNPRFromBase,
  buildThemeWidgetStylesUsLWFromBase,
  buildCoreWidgets,
  buildProductCardWidgets,
  buildProductWidgets,
  buildSearchWidgets,
  buildMiniCartWidgets,
  buildAccountWidgets,
  buildLandingpageWidgets,
} from './themeFoundation';

export const buildThemeVarsUsNPR = (
  vars: Pick<Theme, CommonThemeVars>,
): ReturnType<typeof buildThemeVarsUsNPRFoundation> =>
  buildThemeVarsUsNPRFoundation(vars, {
    gotham: gothamFont.style.fontFamily,
    roboto: robotoFont.style.fontFamily,
  });

export const buildThemeMixinsUsNPR = (
  vars: Omit<Theme, 'mixins' | 'widgets'>,
): ReturnType<typeof buildThemeMixinsUsNPRFoundation> => buildThemeMixinsUsNPRFoundation(vars);

export const buildThemeWidgetStylesUsNPR = (
  vars: Omit<Theme, 'widgets'>,
): ReturnType<typeof buildThemeWidgetStylesUsNPRFoundation> => buildThemeWidgetStylesUsNPRFoundation(vars);

// CMS pages: core + product cards + minicart (header)
export const createCmsPageTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsUsNPR, buildThemeMixinsUsNPR, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesUsNPRFromBase(
      vars,
      buildThemeWidgetStylesUsLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildProductCardWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

// PDP: core + full product + minicart (header)
export const createProductTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsUsNPR, buildThemeMixinsUsNPR, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesUsNPRFromBase(
      vars,
      buildThemeWidgetStylesUsLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildProductWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

// Search/PLP: core + search + product cards + minicart
export const createSearchTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsUsNPR, buildThemeMixinsUsNPR, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesUsNPRFromBase(
      vars,
      buildThemeWidgetStylesUsLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildSearchWidgets(vars),
        ...buildProductWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

// Account: core + account + minicart (header)
export const createAccountTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsUsNPR, buildThemeMixinsUsNPR, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesUsNPRFromBase(
      vars,
      buildThemeWidgetStylesUsLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildAccountWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

// Landing/offer pages: core + landing page + minicart
export const createLandingPageTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsUsNPR, buildThemeMixinsUsNPR, (vars: Omit<Theme, 'widgets'>) =>
    buildThemeWidgetStylesUsNPRFromBase(
      vars,
      buildThemeWidgetStylesUsLWFromBase(vars, {
        ...buildCoreWidgets(vars),
        ...buildLandingpageWidgets(vars),
        ...buildMiniCartWidgets(vars),
      }),
    ),
  );

const createTheme: ThemeCreateFunction = () =>
  buildTheme(buildThemeVarsUsNPR, buildThemeMixinsUsNPR, buildThemeWidgetStylesUsNPR);
export default createTheme;
